import React, {useEffect, useMemo} from 'react';
import {useProviderProfile} from "../../../../ProviderProfileProvider";
import moment from "moment";
import {com, GetEventsRequest, IKensuCalendarEventProto, space} from "../../../../provider_api";
import './Dashboard.css';
import {findClassTemplate, findPractitioner, spanToMinutes} from "../Calendar/CalendarUtil";
import {TabbedPageHeader} from "../../../../components/TabbedPageHeader";
import {useGetStripePayments} from "../../../../util/RpcHooks/GetStripePayments";
import {priceToString} from "../../../../util/PriceUtil";
import {paymentStatusWidget} from "../Billing/Payments";
import {useGetCalendarEvents} from "../../../../util/RpcHooks/GetCalendarEvents";
import GetStripePaymentsRequest = com.onkensu.platform.billing.GetStripePaymentsRequest;
import IApiPaymentProto = space.kenko.proto.IApiPaymentProto;
import {useNavigateWithParams} from "../../../../util/NavUtils";

export const Dashboard = () => {

  const {business, businessProfile} = useProviderProfile();
  const paymentsRpc = useGetStripePayments();
  const calendarEventsRpc = useGetCalendarEvents();
  const navigate = useNavigateWithParams();

  // Returns all INSTANCES of appointments returned from the server
  const getEventInstances = () => {
    const instances: (IKensuCalendarEventProto)[] = [];

    calendarEventsRpc.events.forEach(instance => {
      // Don't bother with availability blocks
      if (instance.appointment || instance.class) {
        instances.push(instance);
      }
    })

    instances.sort((a, b) => {
      return (a.startTimestamp as number) - (b.startTimestamp as number);
    })

    return instances;
  }

  // A predicate which returns whether the appointment is today, or not today
  const dayFilter = (forToday: boolean, instance: IKensuCalendarEventProto) => {
    const today = new Date();
    const startDate = new Date(instance.startTimestamp as number);
    return forToday == ((today.getDay() == startDate.getDay()) &&
        (today.getMonth() == startDate.getMonth()) &&
        (today.getFullYear() == startDate.getFullYear()));
  }

  const todaysAppointments = useMemo(() => {
    return getEventInstances().filter(instance => dayFilter(true, instance));
  }, [calendarEventsRpc.events])

  const upcomingAppointments = useMemo(() => {
    return getEventInstances()
        .filter(instance => dayFilter(false, instance))
        // Maybe there are edge cases where appointments could be in the past? Not sure... 
        .filter(instance => (instance.startTimestamp as number) > new Date().getTime());
  }, [calendarEventsRpc.events])


  // Send off two rpcs - one for calendar events, one for payments
  useEffect(() => {

    calendarEventsRpc.send(new GetEventsRequest({
      businessId: business?.businessId,
      startTimestampMs: moment().startOf('day').valueOf(), // valueOf gets the unix epoch millis
      endTimestampMs: moment().add(7, 'days').startOf('day').valueOf()
    }));

    paymentsRpc.send(new GetStripePaymentsRequest({
      limit: 10
    }));
  }, [])

  const eventBox = (event: IKensuCalendarEventProto) => {

    const eventStart = new Date(event.startTimestamp as number)
    const eventLength = spanToMinutes(event.startTimestamp as number, event.endTimestamp as number)

    return (<div className={'DashboardItem'}>
      <div className={'DashboardItemLeft'}>
        <h2>{eventStart.getDate()}</h2>
        <span>{eventStart.toLocaleDateString(undefined, {month: 'short'})}</span>
      </div>
      <div className={'DashboardItemRight'}>
        <span>{eventStart.toLocaleTimeString(undefined, {hour: "numeric", minute: 'numeric'})}</span>
        <span><b>{getEventTitle(event)}</b></span>
        <span>{getPractitionerName(event)}&nbsp;•&nbsp;{eventLength} min</span>
        {event.appointment?.booking?.details?.service?.virtualMeetingLink && <span>
          <a style={{textDecoration: 'underline'}} target={'_blank'}
             href={event.appointment?.booking?.details?.service?.virtualMeetingLink}>
          (Virtual meeting link)
          </a>
        </span>}
      </div>
    </div>);
  }

  const paymentBox = (payment: IApiPaymentProto) => {

    const paymentDate = new Date(payment.createdTimestampMs as number)

    return (<div style={{cursor: 'pointer'}}
                 onClick={() => navigate('/home/billing/payments/' + payment.paymentId)}
                 className={'DashboardItem'}>
      <div className={'DashboardItemLeft'}>
        <h2>{paymentDate.getDate()}</h2>
        <span>{paymentDate.toLocaleDateString(undefined, {month: 'short'})}</span>
      </div>
      <div className={'DashboardItemRight'}>
        <span><b>{payment.purchasedThingName} ({priceToString(payment.price, true)})</b></span>
        <span>{payment.userName}</span>
        <div className={'HorizontalFlex'}
             style={{alignItems: 'center', gap: 5}}>Status: {paymentStatusWidget(payment)}</div>
      </div>
    </div>);
  }

  const getEventTitle = (event: IKensuCalendarEventProto) => {
    if (event.appointment) {
      return event.appointment.booking?.details?.service?.serviceName + " with " + event.appointment.booking?.details?.clientName;
    }

    if (event.class) {

      if (event.class.details?.classTemplateId) {
        return findClassTemplate(businessProfile, event.class.details?.classTemplateId)?.name;
      }

      return event.class.details?.className
    }

    return '';
  }

  const getPractitionerName = (event: IKensuCalendarEventProto) => {
    if (event.appointment) {
      let teacher = findPractitioner(businessProfile?.practitioners,
          event.appointment?.booking?.details?.service?.practitionerId);
      return teacher?.firstName + ' ' + teacher?.lastName;
    }

    if (event.class) {
      let teacher = findPractitioner(businessProfile?.practitioners,
          event.class.details?.teacherPractitionerId);
      return teacher?.firstName + ' ' + teacher?.lastName;
    }

    return '';
  }

  return <div className={'NonScrollingToolPage'}>

    <TabbedPageHeader pageTitle={'Dashboard'}/>

    <div className={'ProviderToolAreaContent'}>

      <div className={'DashboardMain'}>

        <div className={'DashboardColumn'}>

          <div className={'DashboardBox'}>
            <div className={'DashboardBoxHeader'}>
              TODAY'S SCHEDULE
            </div>

            <div className={'DashboardItemsList'}>
              {calendarEventsRpc.rpcState.state == 'pending' && <div>Loading...</div>}
              {calendarEventsRpc.rpcState.state == 'success' && <>
                {todaysAppointments.map(eventBox)}
                {todaysAppointments.length == 0 && <div className={'DashboardItem'}>No events found</div>}
              </>}
              {calendarEventsRpc.rpcState.state == 'error' &&
                  <div className={'ErrorText'}>{calendarEventsRpc.rpcState.message}</div>}
            </div>
          </div>

          <div className={'DashboardBox'}>
            <div className={'DashboardBoxHeader'}>
              UPCOMING EVENTS
            </div>
            Next 7 days

            <div className={'DashboardItemsList'}>
              {calendarEventsRpc.rpcState.state == 'pending' && <div>Loading...</div>}
              {calendarEventsRpc.rpcState.state == 'success' && <>
                {upcomingAppointments.map(eventBox)}
                {upcomingAppointments.length == 0 && <div className={'DashboardItem'}>No events found</div>}
              </>}
              {calendarEventsRpc.rpcState.state == 'error' &&
                  <div className={'ErrorText'}>{calendarEventsRpc.rpcState.message}</div>}
            </div>
          </div>

        </div>

        <div className={'DashboardColumn'}>
          <div className={'DashboardBox'}>
            <div className={'DashboardBoxHeader'}>
              RECENT TRANSACTIONS
            </div>

            <div className={'DashboardItemsList'}>
              {paymentsRpc.rpcState.state == 'pending' && <div>Loading...</div>}
              {paymentsRpc.rpcState.state == 'success' && <>
                {paymentsRpc.payments?.map(paymentBox)}
                {paymentsRpc.payments?.length == 0 && <div className={'DashboardItem'}>No transactions found</div>}
              </>}
              {paymentsRpc.rpcState.state == 'error' &&
                  <div className={'ErrorText'}>{paymentsRpc.rpcState.message}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
}